// Apolo IDE — PanelToggle (v1.6.3 — header-improved).
//
// Single tab button used in SessionWorkspace header. Extracted from inline
// definition in v1.5.9 polish so it can also be reused inside the overflow
// popover. Triple-cue active state: bg tint + accent text + underline.
//
// v1.6.3 improvements (brainstorming + ui-ux-pro-max + shad-pro + tastedesign + impeccable):
// - Hover micro-lift via translateY(-1px) — adds tactile feedback (impeccable)
// - kbd shortcut chip visible on hover (not just HTML title) — ui-ux-pro-max affordance
// - Active state glow via box-shadow ring — tastedesign refined > decorative
// - Icon scale-105 on hover — micro-interaction sutil
// - motion-reduce friendly (transitions disabled when prefers-reduced-motion)
//
// Sizing/spacing tokens chosen for legibility — 14px icon (was 11px),
// 13px font-medium (was 12px tertiary), 6/10px padding (was 4/8).
// WCAG AA contrast verified against apolo-textSecondary (#a1a1a1) on
// apolo-surface1 (#0d0d0d) = 6.05:1.

import clsx from 'clsx';

export default function PanelToggle({
  icon: Icon,
  label,
  shortcut,
  onClick,
  active = false,
  showLabel = true,
  size = 'md',
  showShortcutOnHover = false,
}) {
  const isCompact = size === 'sm';
  return (
    <button
      type="button"
      onClick={onClick}
      title={`${label}${shortcut ? ` (${shortcut})` : ''}`}
      aria-label={`${label}${shortcut ? ` — ${shortcut}` : ''}`}
      aria-current={active ? 'page' : undefined}
      data-active={active ? 'true' : 'false'}
      className={clsx(
        'group relative inline-flex items-center gap-1.5 rounded-md',
        // Sprint v9 M3: min-h 44px mobile (Apple HIG touch target floor),
        // densidade desktop preservada via lg: override.
        'min-h-[44px] lg:min-h-0',
        'transition-all duration-150 motion-reduce:transition-none',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-apolo-accent/50 focus-visible:ring-offset-1 focus-visible:ring-offset-apolo-surface1',
        isCompact ? 'px-2 py-1 text-[12px]' : 'px-2.5 py-1.5 text-[13px]',
        'font-medium',
        active
          ? [
              'bg-apolo-accent/12 text-apolo-accent',
              'shadow-[inset_0_0_0_1px_rgba(59,130,246,0.30),0_0_0_1px_rgba(59,130,246,0.10)]',
            ].join(' ')
          : [
              'text-apolo-textSecondary',
              'hover:-translate-y-px hover:bg-apolo-surface2 hover:text-apolo-textPrimary hover:shadow-sm',
              'active:translate-y-0 active:scale-[0.97]',
              'motion-reduce:hover:translate-y-0',
            ].join(' '),
      )}
    >
      {Icon && (
        <Icon
          size={isCompact ? 13 : 14}
          aria-hidden="true"
          className={clsx(
            'shrink-0 transition-all duration-150 motion-reduce:transition-none',
            active
              ? 'text-apolo-accent'
              : 'text-apolo-textTertiary group-hover:scale-105 group-hover:text-apolo-textSecondary',
          )}
        />
      )}
      {showLabel && <span className="truncate">{label}</span>}
      {showShortcutOnHover && shortcut && (
        <kbd
          aria-hidden="true"
          className={clsx(
            'ml-0.5 hidden shrink-0 rounded border border-apolo-borderSubtle bg-apolo-surface3/70 px-1 py-0 font-mono text-[10px] leading-none text-apolo-textTertiary',
            'group-hover:inline-flex group-focus-visible:inline-flex',
            active && 'border-apolo-accent/20 bg-apolo-accent/10 text-apolo-accent/80',
          )}
        >
          {shortcut}
        </kbd>
      )}
      {/* Underline indicator — animates from center on active */}
      <span
        aria-hidden="true"
        className={clsx(
          'pointer-events-none absolute inset-x-2 bottom-0 h-[2px] origin-center rounded-full bg-apolo-accent transition-transform duration-200 motion-reduce:transition-none',
          active ? 'scale-x-100' : 'scale-x-0',
        )}
      />
    </button>
  );
}

/** Menu row variant for PanelGroupOverflow — same visual language as PanelToggle. */
export function PanelToggleMenuItem({ tab, active, onClick }) {
  const Icon = tab.icon;
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      aria-label={tab.shortcut ? `${tab.label} — ${tab.shortcut}` : tab.label}
      aria-current={active ? 'page' : undefined}
      className={clsx(
        'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[13px] font-medium transition',
        active
          ? 'bg-apolo-accent/12 text-apolo-accent'
          : 'text-apolo-textSecondary hover:bg-apolo-surface2 hover:text-apolo-textPrimary',
      )}
    >
      {Icon ? <Icon size={14} aria-hidden="true" className="shrink-0" /> : null}
      <span className="min-w-0 flex-1 truncate">{tab.label}</span>
      {tab.shortcut ? (
        <kbd className="shrink-0 rounded border border-apolo-borderSubtle bg-apolo-surface3/70 px-1 py-0 font-mono text-[10px] text-apolo-textTertiary">
          {tab.shortcut}
        </kbd>
      ) : null}
    </button>
  );
}
